import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import {BoxProps, useMediaQuery} from '@chakra-ui/react';
import {UiBox, UiButton, UiCenter, UiContainer, UiIcon, UiSimpleGrid, UiText} from '~/components/elements';
import {IoIosArrowBack} from 'react-icons/all';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '~/redux/root-reducer';
import {setBackQuestionnaire, setNextQuestionnaire} from '~/redux/questionnaires/slice';
import {AppDispatch} from '~/redux/root-store';
import {QUERY_MOBILE} from '~/constants';
import {Action, AnswerData, MedicalQuestionnaireKey} from '~/redux/questionnaires/types';
import {UiImage} from '~/components/elements/UiImage';
import {Row} from '~/components/elements/grid/Row';
import {spacing} from '~/utils/theme/spacing';
import {
  EASE_LINK,
  PROGRESS_DATA,
  SPEAK_TO_CARE_TEAM_DEEPLINK,
  SPEAK_TO_CARE_TEAM_LINK,
  TELEHEALTH_DEEPLINK,
} from '~/constants/medical-questionnaires';
import {Col} from '~/components/elements/grid/Col';
import {redirectInIframe, triggerChat} from '~/utils/helpers';
import {getCurrentUserFromStorage} from '~/services/auth';
import {trackMedicalQuestionnaireScreen} from '~/redux/questionnaires/thunk';
import {useRequestState} from '~/hooks';

interface Props extends BoxProps {
  isActive?: boolean;
  backIcon?: boolean;
  children: React.ReactNode;
  containerClasses?: string;
  handleOnNext?: () => void;
  handleGoBack?: () => void;
  buttonTittle?: string;
  disableNext?: boolean;
  nextScreenKey?: MedicalQuestionnaireKey;
  checkoutStep?: number;
  actions?: Action[];
  loading?: boolean;
  isShowLogin?: boolean;
  hasFloatActionButtons?: boolean;
}

export const UiQuestionnaireLayout = memo(function UiQuestionnaireLayout({
  containerClasses,
  children,
  backIcon,
  handleOnNext,
  handleGoBack,
  buttonTittle = 'Continue',
  disableNext = false,
  nextScreenKey,
  checkoutStep,
  actions,
  loading = false,
  isShowLogin,
  hasFloatActionButtons = false,
  ...rest
}: Props) {
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  const dispatch: AppDispatch = useDispatch();
  const activeQuestion = useSelector((state: RootState) => state.questionnaires.activeQuestion);
  const isWebview = useSelector((state: RootState) => state.questionnaires.isWebview);
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const [redirectLoading, setRedirectLoading] = React.useState(false);
  // const methodKey = useSelector((state: RootState) => state.questionnaires.methodKey);
  // const {loading: loadingTracking} = useRequestState(trackMedicalQuestionnaireScreen);
  // const mdqId = medicalQuestionnaire?.mdqID ? medicalQuestionnaire.mdqID : '';

  // const currentAnswer = useSelector(
  //   (state: RootState) =>
  //     // @ts-ignore
  //     state.questionnaires?.collectData[activeQuestion.keys[activeQuestion.keys.length - 2]]?.data || {},
  // );
  // const rootCollectData = useSelector(
  //   (state: RootState) =>
  //     // @ts-ignore
  //     state.questionnaires.medicalQuestionnaire.collectData[activeQuestion.keys[activeQuestion.keys.length - 2]]
  //       ?.data || {},
  // );
  // const finalCollectData = useMemo(() => {
  //   let newRootCollectData = {...rootCollectData};
  //   if (newRootCollectData?.user && currentAnswer?.user) {
  //     const profile = Object.keys(newRootCollectData.user).reduce((obj: Record<string, string>, key) => {
  //       if (key in currentAnswer.user) {
  //         obj[key as keyof typeof obj] = currentAnswer.user[key];
  //       }
  //       return obj;
  //     }, {});
  //     return {
  //       user: profile,
  //     };
  //   } else {
  //     return Object.keys(newRootCollectData).reduce((obj: Record<string, string>, key) => {
  //       if (key in currentAnswer) {
  //         obj[key as keyof typeof obj] = currentAnswer[key];
  //       }
  //       return obj;
  //     }, {});
  //   }
  // }, [currentAnswer, rootCollectData]);
  const onClickLogin = () => {
    setRedirectLoading(true);
    if (wpAuthUser?.baseURL && wpAuthUser?.currentURL) {
      redirectInIframe(wpAuthUser.baseURL + '/login?redirect_to=' + wpAuthUser?.currentURL, '_self');
    }
  };

  const renderActionButtons = () => {
    return (
      <>
        {actions?.map(item => {
          const isShowChat = item?.userAction?.value === 'SHOW_CHAT';
          return (
            <UiButton
              key={item.value}
              disabled={disableNext || redirectLoading || loading}
              onClick={() => nextScreen(item)}
              title={item.label}
              minW="150px"
              _hover={{
                background: 'baseColor',
                color: 'white',
              }}
              isLoading={loading}
              alignItems="center"
              justifyContent="center"
              display="flex"
              bg={isShowChat ? 'fadedJade' : undefined}
            />
          );
        })}
      </>
    );
  };

  const renderActionContainer = (isShowFloatBtn: boolean) => {
    if (isShowFloatBtn) {
      return (
        <Row
          width={{base: '100%', md: 'auto'}}
          style={{position: 'fixed', bottom: 2, left: '50%', transform: 'translateX(-50%)', justifyContent: 'center'}}
        >
          {renderActionButtons()}
        </Row>
      );
    }
    return (
      <UiSimpleGrid columns={1} spacing={actions && actions?.length > 1 ? 5 : 0} width={{base: '100%', md: 'auto'}}>
        {renderActionButtons()}
        {isShowLogin && (
          <UiButton
            onClick={onClickLogin}
            title="Login / Sign up"
            minW="150px"
            _hover={{
              background: 'baseColor',
              color: 'white',
            }}
            alignItems="center"
            justifyContent="center"
            display="flex"
            mt={2}
            isLoading={redirectLoading}
          />
        )}
      </UiSimpleGrid>
    );
  };

  const nextScreen = async (item: Action) => {
    // item.userAction.params
    switch (item.userAction.value) {
      case 'NEXT':
      case 'FORM_SUBMIT_AND_NEXT':
        if (nextScreenKey) {
          dispatch(setNextQuestionnaire({value: nextScreenKey}));
        }
        if (handleOnNext) {
          handleOnNext();
          // dispatch(
          //   trackMedicalQuestionnaireScreen({
          //     data: {
          //       methodKey,
          //       mdqKey: mdqId,
          //       screen: activeQuestion.keys[activeQuestion.keys.length - 2],
          //       wpAuthUser,
          //       collectData: finalCollectData,
          //     },
          //   }),
          // );
        }
        break;
      case 'NAVIGATE_TO':
        window.location.href = item?.userAction?.params
          ? `/?methodKey=${item.userAction.params}&token=${savedToken}${isWebview ? '&isWebview=true' : ''}`
          : window.location.href;
        break;
      case 'EXIT':
        if (isWebview) {
          window.open(TELEHEALTH_DEEPLINK, '_self');
        } else {
          redirectInIframe(EASE_LINK, '_blank');
        }
        break;
      case 'SHOW_CHAT':
        if (isWebview) {
          window.open(SPEAK_TO_CARE_TEAM_DEEPLINK, '_self');
        } else {
          triggerChat();
        }
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Not implement yet!');
        break;
    }
  };

  const prevScreen = () => {
    if (handleGoBack) {
      handleGoBack();
    }
    dispatch(setBackQuestionnaire({}));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <UiContainer
      maxW="container.md"
      height="100%"
      position="relative"
      p="20px"
      pt="5px"
      pb={0}
      display="flex"
      flexDirection="column"
      style={{flex: '1 0 auto', WebkitBoxFlex: 1, display: '-webkit-flex'}}
      {...rest}
    >
      {activeQuestion.keys.length > 1 && (
        <UiIcon as={IoIosArrowBack} boxSize={'20px'} mt={6} onClick={prevScreen} cursor="pointer" position="absolute" />
      )}
      <UiCenter>
        <UiImage type="icon" name="ease-logo" height="20px" width="92px" mt={6} />
      </UiCenter>
      <Col flex={1}>
        {checkoutStep && (
          <UiCenter>
            <Row justifyContent="start" alignItems="center" overflowX="auto" mt={spacing.small} pb={spacing.tiny}>
              {PROGRESS_DATA.map(i => (
                <UiText
                  textDecoration={checkoutStep === i.value ? 'underline' : 'none'}
                  textAlign="center"
                  textColor="fadedJade"
                  fontWeight="700"
                  fontSize="xs"
                  key={i.value}
                  mx={{base: 5, md: 20}}
                >
                  {i.label}
                </UiText>
              ))}
            </Row>
          </UiCenter>
        )}

        <Col
          id="qns-layout"
          className={classNames(`animate__animated animate__fadeInLeft`, containerClasses)}
          pb="20px"
          flex={1}
          // flexGrow={1}
        >
          {children}
        </Col>
        <UiBox display="flex" justifyContent="center" alignItems="center" pt={0} pb={8} w="100%" bg="primaryBackground">
          {renderActionContainer(hasFloatActionButtons)}
        </UiBox>
      </Col>
    </UiContainer>
  );
});
